import * as React from "react";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import Button from "@mui/material/Button";
import { FormikValues, useFormik } from "formik";
import { GoogleLogin } from "@react-oauth/google";
import toast from "react-hot-toast";

import {
  Alert,
  AlertTitle,
  Checkbox,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { Dispatch } from "@reduxjs/toolkit";
import { connect, useDispatch, useSelector } from "react-redux";
import { loginSuccess, setToggleAuthModal } from "../../../Redux/authReducer";
import { loginYupValidation, PASSWORD_RULES } from "../../../yup/login.yup";
import {
  usePostLoginMutation,
  usePostRegsiterUserMutation,
  useValidateGoogleLoginTokenMutation,
  useVerifyInviteLinkMutation,
  useResendVerificationMutation,
  useForgotPasswordMutation,
} from "../../../Services/api.auth.slice";
import { RootState } from "../../../Store/store";
import {
  useLazyGetUserPropertyListQuery,
  useLazyGetWishlistQuery,
} from "../../../Services/api.user.slice";
import LoaderOverlay from "../../Loader/Loader";
import {
  Close,
  ArrowBack,
  Visibility,
  VisibilityOff,
  CheckCircle,
  RadioButtonUnchecked,
  MarkEmailReadOutlined,
} from "@mui/icons-material";
import { saveToken } from "../../../Utils/tokenStorage";
import { visuallyHidden } from "@mui/utils";

interface LoginModalProps {}

const LoginModal = () => {
  const dispatch = useDispatch();
  const [tabIndex, setTabIndex] = React.useState(0);
  const [showForgotPassword, setShowForgotPassword] = React.useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = React.useState("");
  const [showPassword, setShowPassword] = React.useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = React.useState(false);
  const [loginUser] = usePostLoginMutation();
  const [validateToken] = useVerifyInviteLinkMutation();
  const [forgotPassword, { isLoading: isLoadingForgotPassword }] = useForgotPasswordMutation();
  const [resendVerification] = useResendVerificationMutation();

  // After a signup: the address we just emailed. Shows the "Check your email"
  // screen instead of flipping to the Login tab — that flip is what sent people
  // straight to a sign-in that can't work until the link is clicked.
  const [checkEmailFor, setCheckEmailFor] = React.useState<string | null>(null);
  // After a login whose password was right but whose email isn't confirmed yet.
  const [unconfirmedEmail, setUnconfirmedEmail] = React.useState<string | null>(null);
  const [resendState, setResendState] = React.useState<"idle" | "sending" | "sent">("idle");

  // Google draws its button at a fixed pixel width. Size it to the form, so it
  // lines up with the fields and doesn't run past the dialog on a phone (it
  // was a fixed 320px). Google allows 200 to 400.
  const [googleWidth, setGoogleWidth] = React.useState(320);
  const googleObserver = React.useRef<ResizeObserver | null>(null);
  const googleBoxRef = React.useCallback((el: HTMLDivElement | null) => {
    googleObserver.current?.disconnect();
    googleObserver.current = null;
    if (!el) return;
    const fit = () => setGoogleWidth(Math.max(200, Math.min(400, Math.floor(el.clientWidth))));
    fit();
    if (typeof ResizeObserver !== "undefined") {
      googleObserver.current = new ResizeObserver(fit);
      googleObserver.current.observe(el);
    }
  }, []);

  const resendLink = async (email: string) => {
    if (resendState === "sending") return;
    setResendState("sending");
    try {
      await resendVerification({ email }).unwrap();
      setResendState("sent");
    } catch (error: any) {
      setResendState("idle");
      toast.error(error?.data?.errors?.message || "Couldn't send that. Try again in a moment.");
    }
  };

  const params = new URLSearchParams(window.location.search);
  const token = params.get("token");
  const email = params.get("email");
  const isResetPasswordPage = window.location.pathname === "/reset-password";

  React.useEffect(() => {
    // Close modal and skip email verification if we're on the reset password page
    if (isResetPasswordPage) {
      dispatch(setToggleAuthModal(false));
      return;
    }

    async function verifyToken() {
      if (token && email) {
        const tokenRes = await validateToken({ token, email });
        const { data, error } = tokenRes;
        if (error) {
          const { data: errorData } = error as any;
          toast.error(
            errorData?.errors?.message || "Token verification failed"
          );
          return;
        }
        if (data) {
          toast.success(data?.message || "Token verified successfully");
          // Save token to localStorage BEFORE dispatching and API calls
          saveToken(data?.data?.user?.token);
          dispatch(loginSuccess(data?.data));
          initiateLoginApis();
        }
      }
    }

    verifyToken();
  }, [token, email, isResetPasswordPage, dispatch, validateToken]);

  const [registeUser, { isLoading: isLoadingRegister }] =
    usePostRegsiterUserMutation();
  const [fetchPropertyList] = useLazyGetUserPropertyListQuery();
  const [getWishlist] = useLazyGetWishlistQuery();
  const [validateGoogleLoginToken, { isLoading: isLoadingGoogle }] =
    useValidateGoogleLoginTokenMutation();

  const { toggleAuthModal } = useSelector(
    (state: RootState) => state?.auth
  );

  const initiateLoginApis = () => {
    dispatch(setToggleAuthModal(false));
    fetchPropertyList("");
    getWishlist("");
  };

  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
      rememberMe: false,
      confirmPassword: "",
      isRegister: false,
    },
    validationSchema: loginYupValidation,
    onSubmit: async (values) => {
      handleOnSubmit(values);
    },
  });

  const handleOnSubmit = async (values: FormikValues) => {
    const email = String(values.email || "").trim();
    try {
      if (!formik.values.isRegister) {
        setUnconfirmedEmail(null);
        const response = await loginUser({ email, password: values.password });
        if (response.data && !response.data.isApiError) {
          const token = response.data.data?.user?.token;
          // Save token to localStorage BEFORE dispatching and API calls
          saveToken(token);
          toast.success(`Welcome, You have logged in successfully`);
          dispatch(loginSuccess(response.data.data));
          initiateLoginApis();
        } else if (response.data?.isApiError) {
          toast.error(response.data?.errors?.message || "Login failed");
        } else if (response.error) {
          const { status, data } = response.error as any;
          // Right password, email not confirmed: say so in the form, with a way
          // to get a new link, rather than a toast that reads like a typo.
          if (status === 403 && data?.errors?.errorCode === "EMAIL_NOT_VERIFIED") {
            setResendState("idle");
            setUnconfirmedEmail(email);
            return;
          }
          toast.error(data?.errors?.message || "Login failed");
        }
      }
      if (formik?.values?.isRegister) {
        const { data } = await registeUser({
          email,
          password: formik?.values?.password,
          confirmPassword: formik?.values.confirmPassword,
        }).unwrap();
        if (data) {
          // The server answers the same whether or not the address already had
          // an account, so this screen has to make sense either way.
          setResendState("idle");
          setCheckEmailFor(email);
          formik.resetForm();
          return;
        }
      }
    } catch (error: any) {
      const { data } = error;
      const { isApiError } = data || {};
      if (isApiError) {
        toast.error(data?.errors?.message);
        return;
      }
      console.info(error);
    }
  };

  const handleClose = () => {
    dispatch(setToggleAuthModal(false));
    setShowForgotPassword(false);
    setForgotPasswordEmail("");
    setCheckEmailFor(null);
    setUnconfirmedEmail(null);
  };

  // From the "Check your email" screen to the Login tab, address filled in.
  const goToSignIn = (email: string) => {
    setCheckEmailFor(null);
    setTabIndex(0);
    formik.resetForm({ values: { ...formik.initialValues, email, isRegister: false } });
  };

  const goToSignUp = () => {
    setCheckEmailFor(null);
    setTabIndex(1);
    formik.resetForm({ values: { ...formik.initialValues, isRegister: true } });
  };

  const handleForgotPassword = async () => {
    if (!forgotPasswordEmail) {
      toast.error("Please enter your email address");
      return;
    }
    try {
      const response = await forgotPassword({ email: forgotPasswordEmail }).unwrap();
      if (response?.data?.message) {
        toast.success(response.data.message);
        setShowForgotPassword(false);
        setForgotPasswordEmail("");
      }
    } catch (error: any) {
      const errorMessage = error?.data?.errors?.message || "Failed to send reset email";
      toast.error(errorMessage);
    }
  };

  const validateGoogleLoginTokenHandeler = async (credentialResponse: any) => {
    try {
      const { data } = await validateGoogleLoginToken({
        credential: credentialResponse?.credential,
      }).unwrap();
      if (data) {
        // Save token to localStorage BEFORE dispatching and API calls
        saveToken(data?.user?.token);
        
        toast.success(`Welcome, You have logged in successfully`);
        dispatch(loginSuccess(data));
        initiateLoginApis();
      }
    } catch (error: any) {
      const { data } = error;
      const { isApiError } = data;
      if (isApiError) {
        toast.error(data?.errors?.message);
        return;
      }
      console.info(error);
    }
  };

  if (isLoadingGoogle || isLoadingRegister || isLoadingForgotPassword) {
    return <LoaderOverlay />;
  }

  return (
    <Box>
      <Modal
        open={toggleAuthModal}
        onClose={handleClose}
      >
        <Box
          role="dialog"
          aria-modal="true"
          aria-label={
            showForgotPassword ? "Reset password" : checkEmailFor ? "Check your email" : tabIndex === 0 ? "Log in" : "Sign up"
          }
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "90%", sm: 400 },
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
          }}
        >
          <IconButton
            onClick={handleClose}
            sx={{
              position: "absolute",
              top: 10,
              right: 10,
            }}
          >
            <Close />
          </IconButton>

          {showForgotPassword ? (
            <>
              <Box sx={{ display: "flex", alignItems: "center", mt: 3, mb: 2 }}>
                <IconButton onClick={() => setShowForgotPassword(false)} sx={{ mr: 1 }}>
                  <ArrowBack />
                </IconButton>
                <Typography variant="h6">Reset Password</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Enter your email address and we'll send you a link to reset your password.
              </Typography>
              <TextField
                fullWidth
                label="Email"
                variant="outlined"
                margin="normal"
                value={forgotPasswordEmail}
                onChange={(e) => setForgotPasswordEmail(e.target.value)}
                type="email"
              />
              <Button
                fullWidth
                variant="contained"
                size="large"
                sx={{ mt: 2 }}
                onClick={handleForgotPassword}
                disabled={!forgotPasswordEmail}
              >
                Send Reset Link
              </Button>
            </>
          ) : checkEmailFor ? (
            <Box sx={{ mt: 4, textAlign: "center" }} data-testid="check-email">
              <MarkEmailReadOutlined sx={{ fontSize: 48, color: "primary.main" }} />
              <Typography variant="h6" sx={{ fontWeight: 700, mt: 1 }}>
                Check your email
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                We've sent an email to <b>{checkEmailFor}</b>. Click the link in it to confirm your
                address and finish signing up. The link works for 24 hours.
              </Typography>
              <Alert severity="info" sx={{ mt: 2, textAlign: "left" }}>
                You'll be able to sign in once you've clicked that link. If this address already
                has an account, the email tells you how to sign in instead.
              </Alert>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                {resendState === "sent"
                  ? "Sent again. Use the newest email: it replaces the earlier link."
                  : "Nothing yet? Check your spam folder, or"}
              </Typography>
              {resendState !== "sent" && (
                <Button
                  onClick={() => resendLink(checkEmailFor)}
                  disabled={resendState === "sending"}
                  data-testid="check-email-resend"
                >
                  {resendState === "sending" ? "Sending…" : "Send the email again"}
                </Button>
              )}
              <Button
                fullWidth
                variant="contained"
                size="large"
                sx={{ mt: 2 }}
                onClick={() => goToSignIn(checkEmailFor)}
                data-testid="check-email-signin"
              >
                I've confirmed my email: log in
              </Button>
              <Button fullWidth sx={{ mt: 1 }} onClick={goToSignUp}>
                Use a different email
              </Button>
            </Box>
          ) : (
            <>
              <Tabs
                value={tabIndex}
                onChange={(_, newIndex) => {
                  setTabIndex(newIndex);
                  setUnconfirmedEmail(null);
                  if (newIndex === 0) formik.setFieldValue("isRegister", false);
                  if (newIndex === 1) formik.setFieldValue("isRegister", true);
                }}
                centered
                sx={{
                  mt: 5,
                  "&& .MuiTab-root": {
                    alignItems: "baseline",
                  },
                  ".MuiTab-wrapper": {
                    alignItems: "flex-start",
                    justifyContent: "flex-end",
                  },
                }}
              >
                <Tab label="Login" />
                <Tab label="Register" />
              </Tabs>

              <form onSubmit={formik.handleSubmit}>
                <TextField
                  fullWidth
                  // The label says which form this is (Asher, October 2026): the
                  // two tabs looked the same apart from an extra password field.
                  // Pinned above the box so the longer text never gets cut off.
                  label={
                    tabIndex === 0 ? "Welcome back! Log in with your email" : "We're excited to sign you up! Enter your email"
                  }
                  InputLabelProps={{ shrink: true }}
                  placeholder="you@example.com"
                  variant="outlined"
                  margin="normal"
                  name="email"
                  type="email"
                  autoComplete="email"
                  value={formik.values.email}
                  onChange={(e) => {
                    formik.handleChange(e);
                    setUnconfirmedEmail(null); // that advice was about the address just tried
                  }}
                  onBlur={formik.handleBlur}
                  error={formik.touched.email && Boolean(formik.errors.email)}
                  helperText={(formik.touched.email && formik.errors.email) || " "}
                />
                <TextField
                  fullWidth
                  label="Password"
                  InputLabelProps={{ shrink: true }} // sits like the email label above it
                  variant="outlined"
                  margin="normal"
                  type={showPassword ? "text" : "password"}
                  name="password"
                  autoComplete={tabIndex === 1 ? "new-password" : "current-password"}
                  value={formik.values.password}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.password && Boolean(formik.errors.password)}
                  helperText={
                    // On Register the checklist below says what's missing.
                    tabIndex === 1 ? " " : (formik.touched.password && formik.errors.password) || " "
                  }
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                {tabIndex === 1 && (
                  <Box
                    component="ul"
                    aria-label="Password requirements"
                    data-testid="password-rules"
                    sx={{ listStyle: "none", p: 0, m: 0, mt: -1.5, mb: 0.5 }}
                  >
                    {PASSWORD_RULES.map((rule) => {
                      const ok = rule.test(formik.values.password || "");
                      return (
                        <Box
                          component="li"
                          key={rule.id}
                          data-ok={ok}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            fontSize: 13,
                            lineHeight: 1.8,
                            color: ok ? "success.main" : "text.secondary",
                          }}
                        >
                          {ok ? (
                            <CheckCircle sx={{ fontSize: 16 }} aria-hidden />
                          ) : (
                            <RadioButtonUnchecked sx={{ fontSize: 16 }} aria-hidden />
                          )}
                          <span>
                            {rule.label}
                            <Box component="span" sx={visuallyHidden}>
                              {ok ? " (done)" : " (missing)"}
                            </Box>
                          </span>
                        </Box>
                      );
                    })}
                  </Box>
                )}
                {tabIndex === 1 && (
                  <TextField
                    fullWidth
                    label="Confirm Password"
                    InputLabelProps={{ shrink: true }}
                    variant="outlined"
                    margin="normal"
                    type={showConfirmPassword ? "text" : "password"}
                    name="confirmPassword"
                    value={formik.values.confirmPassword}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.confirmPassword &&
                      Boolean(formik.errors.confirmPassword)
                    }
                    helperText={
                      (formik.touched.confirmPassword &&
                        formik.errors.confirmPassword) ||
                      " "
                    }
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle confirm password visibility"
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            edge="end"
                          >
                            {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
                {tabIndex === 0 && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="rememberMe"
                        checked={formik.values.rememberMe}
                        onChange={formik.handleChange}
                      />
                    }
                    label="Remember me"
                  />
                )}
                {tabIndex === 0 && unconfirmedEmail && (
                  <Alert
                    severity="warning"
                    // A long address has no natural break; let it wrap rather than clip.
                    sx={{ mt: 1, "& .MuiAlert-message": { minWidth: 0, overflowWrap: "anywhere" } }}
                    data-testid="login-unconfirmed"
                  >
                    <AlertTitle>Confirm your email first</AlertTitle>
                    {resendState === "sent"
                      ? `We've sent a new link to ${unconfirmedEmail}. Check your inbox and spam folder. It replaces the earlier link.`
                      : `Your password is right, but ${unconfirmedEmail} hasn't been confirmed yet. Click the link in the email we sent when you signed up.`}
                    {resendState !== "sent" && (
                      <Box sx={{ mt: 1 }}>
                        <Button
                          color="inherit"
                          size="small"
                          variant="outlined"
                          onClick={() => resendLink(unconfirmedEmail)}
                          disabled={resendState === "sending"}
                          data-testid="login-resend"
                        >
                          {resendState === "sending" ? "Sending…" : "Resend link"}
                        </Button>
                      </Box>
                    )}
                  </Alert>
                )}
                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  sx={{
                    mt: 2,
                  }}
                  disabled={!(formik.isValid && formik.dirty)}
                  onClick={formik.submitForm}
                >
                  {tabIndex === 0 ? "Log in" : "Sign up"}
                </Button>
                {tabIndex === 0 && (
                  <Box sx={{ textAlign: "center", mt: 2 }}>
                    <Button variant="text" onClick={() => setShowForgotPassword(true)}>
                      Forgot your password?
                    </Button>
                  </Box>
                )}
                <Divider sx={{ my: 2 }}>or</Divider>
                <Box ref={googleBoxRef} width="100%">
                  <GoogleLogin
                    onSuccess={(credentialResponse) => {
                      validateGoogleLoginTokenHandeler(credentialResponse);
                    }}
                    onError={() => {
                      toast.error("Login Failed");
                    }}
                    // Google draws its own button; match its wording to the tab.
                    text={tabIndex === 0 ? "signin_with" : "signup_with"}
                    shape="rectangular"
                    size="large"
                    theme="filled_blue"
                    width={googleWidth}
                    auto_select={true}
                  />
                </Box>
              </form>
            </>
          )}
        </Box>
      </Modal>
    </Box>
  );
};

function mapStateToProps(state: RootState) {
  return {};
}

function mapDispatchToProps(dispatch: Dispatch) {
  return {};
}

export default connect(mapStateToProps, mapDispatchToProps)(LoginModal);
